/**
 * Noise Audit Service
 *
 * Pure computation — no LLM, no async, no side effects. Takes raw per-participant
 * vote data and returns a complete SessionNoiseAudit with item profiles, anchor
 * drift analysis, rank uncertainty bands, and statistically tied clusters.
 *
 * Key concepts:
 *  - Coefficient of Variation (CV = σ/μ): the primary noise signal per item
 *  - Rank uncertainty bands: given the noise, what range of ranks is plausible?
 *  - Anchor drift: Pearson correlation between first voter and subsequent voters
 *  - Tied clusters: adjacent-ranked items whose score distributions overlap
 *
 * Kahneman, Sibony & Sunstein (2021): "Noise is the enemy of good judgment.
 * Unlike bias, it hides in the variance between judgments that should be
 * identical — invisible until you look for it."
 */

import type {
  ItemNoiseProfile,
  ItemNoiseCategory,
  ParticipantScore,
  AnchorDriftResult,
  SessionNoiseAudit,
  SessionNoiseLevel,
} from '../types/prioritization/noiseAudit';
import type { ParticipantResponse, ToolResultItem } from '../types/prioritization/toolSession';

// ── Math primitives ────────────────────────────────────────────────────────────

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function stdDev(values: number[], mu?: number): number {
  if (values.length < 2) return 0;
  const m = mu ?? mean(values);
  const variance = values.reduce((sum, v) => sum + Math.pow(v - m, 2), 0) / values.length;
  return Math.sqrt(variance);
}

/** Pearson correlation coefficient between two equal-length arrays */
function pearsonR(xs: number[], ys: number[]): number {
  if (xs.length < 2 || xs.length !== ys.length) return 0;
  const mx = mean(xs), my = mean(ys);
  const num = xs.reduce((s, x, i) => s + (x - mx) * (ys[i] - my), 0);
  const denom = Math.sqrt(
    xs.reduce((s, x) => s + Math.pow(x - mx, 2), 0) *
    ys.reduce((s, y) => s + Math.pow(y - my, 2), 0)
  );
  return denom === 0 ? 0 : Math.round((num / denom) * 100) / 100;
}

function classifyCV(cv: number, n: number): ItemNoiseCategory {
  if (cv < 0.08 && n >= 3) return 'suspicious_uniform';
  if (cv < 0.18) return 'uniform';
  if (cv < 0.35) return 'normal';
  return 'contested';
}

function noiseLevel(index: number): SessionNoiseLevel {
  if (index < 0.12) return 'low';
  if (index < 0.25) return 'healthy';
  if (index < 0.40) return 'elevated';
  return 'high';
}

/** Extract a numeric score from a ToolResultItem (score or Borda-converted rank) */
function extractScore(item: ToolResultItem, totalItems: number): number | null {
  if (item.score !== undefined) return item.score;
  if (item.rank !== undefined) {
    // Borda conversion: rank 1 → totalItems, rank N → 1
    return totalItems - item.rank + 1;
  }
  return null;
}

// ── Rank band computation ─────────────────────────────────────────────────────

/**
 * Given all items with their mean scores and std devs, compute the plausible
 * rank range for each item under a ±1.5σ perturbation.
 *
 * The key insight: if a group's scores for item A have high noise (std dev),
 * then a slightly different composition of participants might have ranked it
 * very differently. The rank band makes this visible.
 */
function computeRankBands(
  profiles: Array<{ itemId: string; mean: number; stdDev: number; nominatedRank: number }>
): Map<string, { optimisticRank: number; pessimisticRank: number }> {
  const SIGMA = 1.5;
  const result = new Map<string, { optimisticRank: number; pessimisticRank: number }>();

  for (const item of profiles) {
    const highScore = item.mean + SIGMA * item.stdDev;
    const lowScore  = Math.max(0, item.mean - SIGMA * item.stdDev);

    // Optimistic rank: how high could this item rank if its score was +1.5σ?
    const optimistic = profiles.filter(
      other => other.itemId !== item.itemId && other.mean > highScore
    ).length + 1;

    // Pessimistic rank: how low could this item rank if its score was -1.5σ?
    const pessimistic = profiles.filter(
      other => other.itemId !== item.itemId && other.mean >= lowScore
    ).length + 1;

    result.set(item.itemId, {
      optimisticRank: optimistic,
      pessimisticRank: pessimistic,
    });
  }

  return result;
}

// ── Tied cluster detection ────────────────────────────────────────────────────

/**
 * Two adjacent-ranked items are "statistically tied" if the difference in their
 * mean scores is less than sqrt(σ_A² + σ_B²) — a simplified Welch criterion.
 * We then group consecutive ties into clusters.
 */
function detectTiedClusters(
  profiles: ItemNoiseProfile[]
): SessionNoiseAudit['tiedClusters'] {
  const sorted = [...profiles].sort((a, b) => a.nominatedRank - b.nominatedRank);
  const clusters: SessionNoiseAudit['tiedClusters'] = [];

  let clusterStart = 0;
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1];
    const b = sorted[i];
    const threshold = Math.sqrt(a.stdDev ** 2 + b.stdDev ** 2);
    const tied = Math.abs(a.mean - b.mean) < threshold;

    if (!tied || i === sorted.length - 1) {
      const clusterEnd = tied ? i : i - 1;
      if (clusterEnd > clusterStart) {
        const clusterItems = sorted.slice(clusterStart, clusterEnd + 1);
        const rankNums = clusterItems.map(p => p.nominatedRank);
        const minR = Math.min(...rankNums), maxR = Math.max(...rankNums);
        clusters.push({
          clusterLabel: `Ranks ${minR}–${maxR} are statistically tied`,
          itemIds: clusterItems.map(p => p.itemId),
          itemTitles: clusterItems.map(p => p.itemTitle),
          explanation:
            `The score distributions for these ${clusterItems.length} items overlap — ` +
            `their ordering reflects noise more than genuine preference differences.`,
        });
      }
      clusterStart = i;
    }
  }

  return clusters;
}

// ── Anchor drift ──────────────────────────────────────────────────────────────

function analyzeAnchorDrift(
  responses: ParticipantResponse[],
  rankedItemIds: string[]
): AnchorDriftResult | null {
  // Only meaningful with 3+ complete participants and submission timestamps
  const complete = responses
    .filter(r => r.isComplete && r.results.length > 0)
    .sort((a, b) => {
      const ta = (r: ParticipantResponse) =>
        (r as unknown as Record<string, string>).submittedAt ?? r.createdAt ?? '';
      return ta(a).localeCompare(ta(b));
    });

  if (complete.length < 3) return null;

  const totalItems = rankedItemIds.length;

  const getScoreVector = (r: ParticipantResponse): number[] =>
    rankedItemIds.map(id => {
      const found = r.results.find(ri => ri.id === id);
      if (!found) return 0;
      return extractScore(found, totalItems) ?? 0;
    });

  const firstVoter = complete[0];
  const firstScores = getScoreVector(firstVoter);

  const followerCorrelations = complete.slice(1).map((r, idx) => ({
    participantId: r.participantId,
    participantName: r.participantName,
    correlationWithFirstVoter: pearsonR(firstScores, getScoreVector(r)),
    submissionOrder: idx + 2,
  }));

  const meanR = mean(followerCorrelations.map(f => f.correlationWithFirstVoter));
  const detected = meanR > 0.80 && complete.length >= 3;

  let interpretation: string;
  if (!detected) {
    interpretation = 'Participants voted independently — no significant pull toward the first voter.';
  } else if (meanR > 0.93) {
    interpretation =
      `Strong anchor effect: followers correlated ${Math.round(meanR * 100)}% with the first voter ` +
      `(${firstVoter.participantName}). This level of convergence is unlikely to arise from independent judgment.`;
  } else {
    interpretation =
      `Moderate anchor effect: followers correlated ${Math.round(meanR * 100)}% with the first voter ` +
      `(${firstVoter.participantName}). Consider re-running with hidden-mode voting to establish an independent baseline.`;
  }

  return {
    detected,
    firstVoterId: firstVoter.participantId,
    firstVoterName: firstVoter.participantName,
    firstVoterSubmittedAt: (firstVoter as unknown as Record<string, string>).submittedAt,
    meanFollowerCorrelation: Math.round(meanR * 100) / 100,
    followerCorrelations,
    interpretation,
  };
}

// ── Precision statement ───────────────────────────────────────────────────────

function buildPrecisionStatement(
  profiles: ItemNoiseProfile[],
  tiedClusters: SessionNoiseAudit['tiedClusters'],
  noiseIdx: number
): string {
  if (profiles.length === 0) return '';

  const totalTied = tiedClusters.reduce((s, c) => s + c.itemIds.length, 0);

  if (tiedClusters.length === 0 && noiseIdx < 0.15) {
    return 'Scoring was unusually consistent — participants aligned closely on the relative ordering.';
  }
  if (totalTied === 0) {
    return `Noise index: ${(noiseIdx * 100).toFixed(0)}%. Each item's rank is statistically distinct from its neighbors.`;
  }
  const fraction = Math.round((totalTied / profiles.length) * 100);
  return (
    `${fraction}% of ranked items (${totalTied} of ${profiles.length}) fall in statistically tied clusters — ` +
    `their ordering reflects noise as much as genuine group preference. ` +
    `The apparent precision of the ranking overstates the team's actual consensus.`
  );
}

// ── Key findings ──────────────────────────────────────────────────────────────

function buildKeyFindings(audit: Omit<SessionNoiseAudit, 'keyFindings' | 'summary'>): string[] {
  const findings: string[] = [];

  const lvl = audit.noiseLevel;
  if (lvl === 'high') {
    findings.push(`High noise (index ${(audit.noiseIndex * 100).toFixed(0)}%): participants disagreed substantially across most items.`);
  } else if (lvl === 'elevated') {
    findings.push(`Elevated noise (index ${(audit.noiseIndex * 100).toFixed(0)}%): some items show significant scoring disagreement.`);
  } else if (lvl === 'low') {
    findings.push(`Low noise (index ${(audit.noiseIndex * 100).toFixed(0)}%): participants were unusually consistent.`);
  }

  if (audit.suspiciouslyUniformItems.length > 0) {
    const titles = audit.suspiciouslyUniformItems.slice(0, 2).map(p => `"${p.itemTitle}"`).join(', ');
    findings.push(
      `${audit.suspiciouslyUniformItems.length} item${audit.suspiciouslyUniformItems.length > 1 ? 's show' : ' shows'} suspiciously uniform scores ` +
      `(${titles}${audit.suspiciouslyUniformItems.length > 2 ? ', …' : ''}) — ` +
      `possible HiPPO effect or social pressure suppressing honest votes.`
    );
  }

  if (audit.contestedItems.length > 0) {
    const titles = audit.contestedItems.slice(0, 2).map(p => `"${p.itemTitle}"`).join(', ');
    findings.push(
      `${audit.contestedItems.length} genuinely contested item${audit.contestedItems.length > 1 ? 's' : ''} ` +
      `(${titles}${audit.contestedItems.length > 2 ? ', …' : ''}) — real disagreement worth surfacing.`
    );
  }

  if (audit.anchorDrift?.detected) {
    findings.push(
      `Anchor drift detected: ${Math.round(audit.anchorDrift.meanFollowerCorrelation * 100)}% ` +
      `mean correlation with first voter (${audit.anchorDrift.firstVoterName}). ` +
      `Enable hidden voting mode in the next session to test for independence.`
    );
  }

  if (audit.tiedClusters.length > 0) {
    findings.push(audit.tiedClusters[0].clusterLabel + ' — the ranking between them is noise, not signal.');
  }

  return findings.slice(0, 4);
}

// ── Public API ─────────────────────────────────────────────────────────────────

export interface NoiseAuditInput {
  sessionId: string;
  toolSessionId: string;
  sessionName: string;
  rankedItemIds: string[];
  rankedItemTitles: Record<string, string>;
  participantResponses: ParticipantResponse[];
}

/**
 * Compute a complete noise audit from raw participant response data.
 * Purely synchronous — call from React components or services alike.
 */
export function computeNoiseAudit(input: NoiseAuditInput): SessionNoiseAudit | null {
  const { rankedItemIds, rankedItemTitles, participantResponses } = input;

  const complete = participantResponses.filter(r => r.isComplete && r.results.length > 0);
  if (complete.length < 2 || rankedItemIds.length === 0) return null;

  const totalItems = rankedItemIds.length;

  // Build score matrix: itemId → per-participant scores
  const scoreMatrix = new Map<string, ParticipantScore[]>();
  for (const id of rankedItemIds) scoreMatrix.set(id, []);

  for (const response of complete) {
    for (const result of response.results) {
      const s = extractScore(result, totalItems);
      if (s !== null && scoreMatrix.has(result.id)) {
        scoreMatrix.get(result.id)!.push({
          participantId: response.participantId,
          participantName: response.participantName,
          score: s,
          submittedAt: (response as unknown as Record<string, string>).submittedAt,
        });
      }
    }
  }

  // Build item profiles
  const profilesRaw: Array<{ itemId: string; mean: number; stdDev: number; nominatedRank: number }> = [];
  const itemProfiles: ItemNoiseProfile[] = [];

  for (let idx = 0; idx < rankedItemIds.length; idx++) {
    const id = rankedItemIds[idx];
    const pScores = scoreMatrix.get(id) ?? [];
    if (pScores.length < 2) continue;

    const scores = pScores.map(p => p.score);
    const mu = mean(scores);
    const sigma = stdDev(scores, mu);
    const cv = mu > 0 ? sigma / mu : 0;

    profilesRaw.push({ itemId: id, mean: mu, stdDev: sigma, nominatedRank: idx + 1 });
    itemProfiles.push({
      itemId: id,
      itemTitle: rankedItemTitles[id] ?? id,
      nominatedRank: idx + 1,
      participantScores: pScores,
      scoreCount: pScores.length,
      mean: Math.round(mu * 10) / 10,
      stdDev: Math.round(sigma * 10) / 10,
      coefficientOfVariation: Math.round(cv * 100) / 100,
      scoreRange: { min: Math.min(...scores), max: Math.max(...scores) },
      noiseCategory: classifyCV(cv, pScores.length),
      rankBand: { optimisticRank: 0, pessimisticRank: 0, isStatisticallyDistinct: false }, // filled below
    });
  }

  // Compute rank bands
  const bandMap = computeRankBands(profilesRaw);
  for (const profile of itemProfiles) {
    const band = bandMap.get(profile.itemId);
    if (!band) continue;
    const neighbor = itemProfiles.find(
      p => p.nominatedRank === profile.nominatedRank + 1 || p.nominatedRank === profile.nominatedRank - 1
    );
    const isDistinct = neighbor
      ? Math.abs(profile.mean - neighbor.mean) > Math.sqrt(profile.stdDev ** 2 + neighbor.stdDev ** 2)
      : true;
    profile.rankBand = { ...band, isStatisticallyDistinct: isDistinct };
  }

  // Aggregate noise index (weighted: top-ranked items get 2×)
  const noiseIdx = (() => {
    let weightedSum = 0, totalWeight = 0;
    for (const p of itemProfiles) {
      const weight = p.nominatedRank <= 3 ? 2 : 1;
      weightedSum += p.coefficientOfVariation * weight;
      totalWeight += weight;
    }
    return totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 100) / 100 : 0;
  })();

  const lvl = noiseLevel(noiseIdx);
  const contested = itemProfiles.filter(p => p.noiseCategory === 'contested');
  const suspicious = itemProfiles.filter(p => p.noiseCategory === 'suspicious_uniform');
  const tiedClusters = detectTiedClusters(itemProfiles);
  const anchorDrift = analyzeAnchorDrift(complete, rankedItemIds);
  const precisionStatement = buildPrecisionStatement(itemProfiles, tiedClusters, noiseIdx);

  const partialAudit = {
    sessionId: input.sessionId,
    toolSessionId: input.toolSessionId,
    sessionName: input.sessionName,
    participantCount: complete.length,
    itemCount: itemProfiles.length,
    computedAt: new Date().toISOString(),
    noiseIndex: noiseIdx,
    noiseLevel: lvl,
    precisionStatement,
    itemProfiles,
    contestedItems: contested,
    suspiciouslyUniformItems: suspicious,
    tiedClusters,
    anchorDrift,
    summary: '',
  };

  const keyFindings = buildKeyFindings(partialAudit);

  const summary =
    lvl === 'high'
      ? `High-noise session: the ranking carries less precision than it appears. ${contested.length} items are genuinely contested and ${suspicious.length} show suspiciously uniform scores.`
      : lvl === 'elevated'
      ? `Moderate noise detected. The top-ranked items are reliable, but several mid-tier items have overlapping score distributions.`
      : lvl === 'low'
      ? `Very low noise: participants aligned closely. Consider whether this reflects genuine consensus or social conformity — check the uniformity flags.`
      : `Healthy noise level: participants showed expected variation. The ranking is reasonably robust.`;

  return { ...partialAudit, keyFindings, summary };
}

/**
 * Noise level descriptors for UI labeling.
 */
export const NOISE_LEVEL_META: Record<SessionNoiseLevel, {
  label: string;
  color: string;
  bg: string;
  border: string;
  description: string;
}> = {
  low: {
    label: 'Low',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    description: 'Unusually high agreement — verify this is genuine, not conformity.',
  },
  healthy: {
    label: 'Healthy',
    color: 'text-green-700',
    bg: 'bg-green-50',
    border: 'border-green-200',
    description: 'Expected variation. The ranking is reasonably robust.',
  },
  elevated: {
    label: 'Elevated',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'Significant disagreement on several items. Investigate before locking in.',
  },
  high: {
    label: 'High',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    description: 'Substantial noise throughout. The apparent ranking precision is an illusion.',
  },
};

export const ITEM_NOISE_META: Record<ItemNoiseCategory, {
  label: string;
  color: string;
  bg: string;
  description: string;
}> = {
  suspicious_uniform: {
    label: 'Suspiciously Uniform',
    color: 'text-violet-700',
    bg: 'bg-violet-50',
    description: 'Score variance is too low for a group this size. Possible HiPPO effect or social pressure.',
  },
  uniform: {
    label: 'Uniform',
    color: 'text-green-700',
    bg: 'bg-green-50',
    description: 'Healthy convergence — participants aligned on this item.',
  },
  normal: {
    label: 'Normal',
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    description: 'Expected variation. No action needed.',
  },
  contested: {
    label: 'Contested',
    color: 'text-red-700',
    bg: 'bg-red-50',
    description: 'High disagreement. This is a signal worth surfacing — real different views exist.',
  },
};
